import { useCallback, useMemo } from 'react';
import { ProfileStandardClaims } from 'oidc-client';
import { getAuthConfig } from '../config/authConfig';

export interface AuthDataHook {
  token: string;
  profile: ProfileStandardClaims | undefined;
}

export function useAuthData(): AuthDataHook {
  const getClientId = useCallback((): string => {
    // Use centralized authConfig instead of manual detection
    const authConfig = getAuthConfig();
    
    // Get provider config based on cloud provider and provider type
    let providerConfig;
    if (authConfig.cloudProvider === 'azure') {
      providerConfig = authConfig.identityProviders.azure?.[authConfig.providerType];
    } else if (authConfig.cloudProvider === 'gcp') {
      // GCP has a single direct provider config
      providerConfig = authConfig.identityProviders.gcp;
    }
    
    if (!providerConfig) {
      throw new Error(`No provider config for ${authConfig.cloudProvider}:${authConfig.providerType}`);
    }
    
    // Build authority:clientId format for session storage key
    // For Azure: authority is already in config
    // For GCP: use 'https://accounts.google.com' as authority
    const authority = providerConfig.authority || 'https://accounts.google.com';
    return `${authority}:${providerConfig.clientId}`;
  }, []);

  const getAccessToken = useCallback((): string => {
    try {
      const raw = sessionStorage.getItem(`oidc.user:${getClientId()}`);
      if (!raw) return '';
      const parsed = JSON.parse(raw || '{}');
      return parsed?.access_token || '';
    } catch {
      return '';
    }
  }, [getClientId]);

  const getProfile = useCallback((): ProfileStandardClaims | undefined => {
    try {
      const raw = sessionStorage.getItem(`oidc.user:${getClientId()}`);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw || '{}');
      return parsed?.profile;
    } catch {
      return undefined;
    }
  }, [getClientId]);

  const token = useMemo(() => getAccessToken(), [getAccessToken]);
  const profile = useMemo(() => getProfile(), [getProfile]);

  return { token, profile };
}

export default useAuthData;